import React from "react";
import { Box, Chip, Divider, IconButton, Typography, Tabs, Tab } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EventIcon from "@mui/icons-material/Event";
import { useGetPlaylistGroupQuery } from "../../../Api/attoDeskApi";
import { IPlaylistGroup } from "../../../Api/Interface/api.interface";
import PageLoader from "../../../Components/Loader/PageLoader";
import ScheduleTab from "../../Playlist/Edit/ScheduleTab";
import MenuRulesTab from "../../Playlist/Edit/MenuRulesTab";

/**
 * Menu Schedule detail / edit. A "menu schedule" is the set of scheduled menus
 * for one playlist group, so this page is keyed by the group id (:id) and
 * reuses the EXISTING scheduling surfaces (ScheduleTab = weekly schedule +
 * editor + preview, MenuRulesTab = priority) rather than duplicating any
 * scheduling logic. The playlist Menu Rules tab remains the playlist-specific
 * view; this is the company/group-level management view.
 *
 * The /edit route renders the same component — editing already happens inline
 * inside ScheduleTab's editor, so there is no separate read-only mode to fork.
 */
const MenuScheduleDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const groupId = Number(id);

  const { data, isLoading } = useGetPlaylistGroupQuery(groupId, { skip: !groupId });
  const group = data?.data as unknown as IPlaylistGroup | undefined;

  const [tab, setTab] = React.useState(0);

  if (isLoading || !group) return <PageLoader height="20vh" />;

  const companyId = group.company_id ?? null;

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      {/* Header */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        px: 3, position: "sticky", top: 0, zIndex: 10,
        boxShadow: "0 4px 30px rgba(0,0,0,0.18)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
          <IconButton onClick={() => navigate("/menu-schedules/list")} size="small"
            sx={{
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2,
              width: 38, height: 38, bgcolor: "rgba(255,255,255,0.05)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
            }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#e2e8f0" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)",
            boxShadow: "0 4px 12px rgba(8,145,178,0.45)",
          }}>
            <EventIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Typography fontSize={20} fontWeight={800} color="#fff" letterSpacing="-0.02em"
                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 360 }}>
                {group.name}
              </Typography>
              {group.company && (
                <Chip size="small" label={group.company.name}
                  sx={{
                    height: 22, fontSize: 11, fontWeight: 700,
                    bgcolor: "rgba(148,163,184,0.18)", color: "#cbd5e1",
                    border: "1px solid rgba(148,163,184,0.3)",
                  }} />
              )}
              <Chip size="small" label={group.is_active ? "Active" : "Inactive"}
                sx={{
                  height: 22, fontSize: 11, fontWeight: 700,
                  bgcolor: group.is_active ? "rgba(16,185,129,0.18)" : "rgba(148,163,184,0.18)",
                  color: group.is_active ? "#a7f3d0" : "#cbd5e1",
                  border: `1px solid ${group.is_active ? "rgba(16,185,129,0.35)" : "rgba(148,163,184,0.3)"}`,
                }} />
            </Box>
            <Typography fontSize={11.5} color="rgba(255,255,255,0.6)" sx={{ mt: 0.5 }}>
              {group.devices_count ?? 0} screen{(group.devices_count ?? 0) === 1 ? "" : "s"} · menu schedule &amp; rules
            </Typography>
          </Box>
        </Box>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)}
          textColor="inherit"
          TabIndicatorProps={{ sx: { bgcolor: "#06b6d4", height: 3 } }}
          sx={{
            minHeight: 44,
            "& .MuiTab-root": {
              color: "rgba(255,255,255,0.6)", textTransform: "none",
              fontWeight: 700, fontSize: 13, minHeight: 44,
            },
            "& .Mui-selected": { color: "#fff !important" },
          }}>
          <Tab label="Schedule" />
          <Tab label="Rule Priority" />
        </Tabs>
      </Box>

      <Box sx={{ p: 3 }}>
        {tab === 0 && (
          <ScheduleTab playlistId={0} groupId={groupId} companyId={companyId} />
        )}
        {tab === 1 && (
          <MenuRulesTab playlistId={0} groupId={groupId} companyId={companyId} />
        )}
      </Box>
    </Box>
  );
};

export default MenuScheduleDetail;
